import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Check, X, Clock, Undo2, ChevronDown, ChevronRight, Search } from "lucide-react";
import { useAppData } from "../data/AppDataContext";
import { api } from "../api/client";
import { naira, formatDate } from "../data/format";
import {getAuthToken} from "../../src/api/client"

function ageInfo(submittedAt) {
  const hours = (Date.now() - new Date(submittedAt).getTime()) / 36e5;
  let text;
  if (hours < 1) text = "Waiting <1h";
  else if (hours < 24) text = `Waiting ${Math.floor(hours)}h`;
  else text = `Waiting ${Math.floor(hours / 24)}d`;
  const tone = hours < 6 ? "amber" : "red";
  return { text, tone };
}

function ReceiptModal({ objectUrl, isPdf, onClose }) {
  function openInNewTab() {
    window.open(objectUrl, "_blank");
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(15,45,99,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}
      onClick={onClose}
    >
      <div
        style={{ background: "var(--white)", borderRadius: 14, maxWidth: 680, width: "100%", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid var(--line)" }}>
          <div style={{ fontWeight: 700, fontSize: 14.5 }}>Receipt</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="pl-btn pl-btn-ghost" onClick={openInNewTab}>
              Open in new tab
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{ border: "none", background: "transparent", fontSize: 18, color: "var(--ink-400)", cursor: "pointer", lineHeight: 1, padding: "4px 8px" }}
            >
              ✕
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 16, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--grey-100, #f1f3f8)" }}>
          {isPdf ? (
            <embed src={objectUrl} type="application/pdf" style={{ width: "100%", height: "70vh", border: "none" }} />
          ) : (
            <img src={objectUrl} alt="Receipt" style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: 8 }} />
          )}
        </div>
      </div>
    </div>
  );
}

function ReceiptThumbnail({ url, onOpen }) {
  const { loading, error, objectUrl, contentType } = useReceiptBlob(url);
  const isPdf = contentType?.includes("pdf");

  if (loading) {
    return (
      <div style={{ width: 128, height: 128, borderRadius: 8, background: "var(--grey-100, #f1f3f8)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "var(--ink-400)", marginTop: 8 }}>
        ...
      </div>
    );
  }
  if (error || !objectUrl) {
    return (
      <div style={{ width: 128, height: 128, borderRadius: 8, background: "var(--red-50, #fdf3f3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "var(--red-700)", marginTop: 8, textAlign: "center", padding: 4 }}>
        Couldn't load
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(objectUrl, isPdf)}
      style={{
        width: 128, height: 128, borderRadius: 8, border: "1px solid var(--line)",
        overflow: "hidden", cursor: "pointer", padding: 0, marginTop: 8, background: "var(--white)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      title="View receipt"
    >
      {isPdf ? (
        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--blue-700)" }}>PDF</div>
      ) : (
        <img src={objectUrl} alt="Receipt thumbnail" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      )}
    </button>
  );
}

function useReceiptBlob(url) {
  const [state, setState] = useState({ loading: true, error: false, objectUrl: null, contentType: null });
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!url || fetchedRef.current) return;
    fetchedRef.current = true;
    const secureUrl = url.replace(/^http:\/\//i, "https://");

    fetch(secureUrl, { headers: { Authorization: `Bearer ${getAuthToken()}` } })
      .then((res) => {
        if (!res.ok) throw new Error(`Could not load receipt (${res.status})`);
        const contentType = res.headers.get("Content-Type") || "";
        return res.blob().then((blob) => ({ blob, contentType }));
      })
      .then(({ blob, contentType }) => {
        const objectUrl = URL.createObjectURL(blob);
        setState({ loading: false, error: false, objectUrl, contentType });
      })
      .catch(() => {
        setState({ loading: false, error: true, objectUrl: null, contentType: null });
      });
  }, [url]);

  return state;
}




export default function Queue() {
  const { pendingPayments, confirmPayment, rejectPayment, revertPayment, loading } = useAppData();
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [undo, setUndo] = useState(null);
  const [expandedCustomerId, setExpandedCustomerId] = useState(null);
  const [openedReceipt, setOpenedReceipt] = useState(null);

  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [historySearch, setHistorySearch] = useState("");

  const toggleHistory = useCallback(() => {
    if (!showHistory && !history) {
      api.paymentHistory().then(setHistory).catch(() => {});
    }
    setShowHistory((v) => !v);
  }, [showHistory, history]);

  const filteredHistory = useMemo(() => {
    if (!history) return null;
    const q = historySearch.trim().toLowerCase();
    if (!q) return history;
    return history.filter((p) => p.customer_name.toLowerCase().includes(q));
  }, [history, historySearch]);

  const grouped = useMemo(() => {
    const map = new Map();
    pendingPayments.forEach((p) => {
      if (!map.has(p.customer_id)) {
        map.set(p.customer_id, { customer_id: p.customer_id, customer_name: p.customer_name, payments: [] });
      }
      map.get(p.customer_id).payments.push(p);
    });
    return Array.from(map.values()).sort((a, b) => b.payments.length - a.payments.length);
  }, [pendingPayments]);

  const totalAmount = pendingPayments.reduce((sum, p) => sum + Number(p.amount), 0);

  function handleOpenReceipt(objectUrl, isPdf) {
    setOpenedReceipt({ objectUrl, isPdf });
  }

  useEffect(() => {
    if (!undo) return;
    if (undo.secondsLeft <= 0) {
      setUndo(null);
      return;
    }
    const t = setTimeout(() => setUndo((u) => (u ? { ...u, secondsLeft: u.secondsLeft - 1 } : u)), 1000);
    return () => clearTimeout(t);
  }, [undo]);





  async function handleConfirm(payment) {
    setBusyId(payment.id);
    try {
      await confirmPayment(payment.id);
      setUndo({ paymentId: payment.id, label: `Confirmed ${naira(payment.amount)} for ${payment.customer_name}`, secondsLeft: 10 });
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(payment) {
    setBusyId(payment.id);
    try {
      await rejectPayment(payment.id, rejectNote);
      setUndo({ paymentId: payment.id, label: `Rejected claim from ${payment.customer_name}`, secondsLeft: 10 });
      setRejectingId(null);
      setRejectNote("");
    } finally {
      setBusyId(null);
    }
  }

  async function handleUndo() {
    if (!undo) return;
    const id = undo.paymentId;
    setUndo(null);
    await revertPayment(id);
  }

  return (
    <div>
      <div className="pl-page-title">Pending queue</div>
      <div className="pl-page-sub">Customer-submitted claims, waiting on a manual match against the bank statement.</div>

      {pendingPayments.length > 0 && (
        <div className="pl-queue-totals">
          {pendingPayments.length} pending - {naira(totalAmount)} to verify
        </div>
      )}

      <button
        className="pl-btn pl-btn-ghost"
        onClick={toggleHistory}
        style={{ margin: "16px 0" }}
      >
        {showHistory ? "Hide confirmation history" : "View confirmation history"}
      </button>

      {showHistory && (
        <div className="pl-panel" style={{ padding: 20, marginBottom: 24 }}>
          <div className="pl-section-title">Confirmation history</div>
          <div className="pl-page-sub" style={{ marginBottom: 14 }}>
            Every payment ever confirmed or rejected, across all customers.
          </div>

          <div className="pl-searchbar" style={{ maxWidth: 420, marginBottom: 16 }}>
            <Search size={15} className="pl-search-icon" />
            <input
              placeholder="Search by customer name..."
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
            />
          </div>

          {!filteredHistory && <div className="pl-empty">Loading...</div>}

          {filteredHistory && filteredHistory.length === 0 && (
            <div className="pl-empty">
              {historySearch ? `No history matches "${historySearch}".` : "No confirmed or rejected payments yet."}
            </div>
          )}

          {filteredHistory && filteredHistory.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {filteredHistory.map((p) => (
                <div
                  key={p.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 14,
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: "1px solid var(--line)",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <Link
                      to={`/customers/${p.customer_id}`}
                      style={{ fontWeight: 700, fontSize: 14, color: "inherit", textDecoration: "none" }}
                    >
                      {p.customer_name}
                    </Link>
                    <div className="pl-meta">
                      {p.equipment_name} - {naira(p.amount)} -{" "}
                      {p.confirmed_at ? formatDate(p.confirmed_at) : formatDate(p.submitted_at)}
                    </div>
                    {p.status === "rejected" && p.note && (
                      <div style={{ fontSize: 12.5, color: "var(--red-700)", marginTop: 4 }}>
                        Reason: {p.note}
                      </div>
                    )}
                  </div>
                  <span className={`pl-chip ${p.status === "confirmed" ? "pl-chip-green" : "pl-chip-red"}`}>
                    {p.status === "confirmed" ? "Confirmed" : "Rejected"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading && pendingPayments.length === 0 && <div className="pl-empty">Loading...</div>}
      {!loading && pendingPayments.length === 0 && (
        <div className="pl-empty">No pending claims right now. New submissions will appear here.</div>
      )}

      {grouped.map((group) => {
        const isExpanded = expandedCustomerId === group.customer_id;
        const groupTotal = group.payments.reduce((s, p) => s + Number(p.amount), 0);
        return (
          <div className="pl-queue-group" key={group.customer_id}>
            <div
              className="pl-row"
              style={{ cursor: "pointer" }}
              onClick={() => setExpandedCustomerId(isExpanded ? null : group.customer_id)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {isExpanded ? <ChevronDown size={16} color="var(--ink-400)" /> : <ChevronRight size={16} color="var(--ink-400)" />}
                <div>
                  <Link
                    to={`/customers/${group.customer_id}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{ fontWeight: 800, fontSize: 14.5, color: "var(--blue-900)", textDecoration: "none" }}
                  >
                    {group.customer_name}
                  </Link>
                  <div className="pl-meta">
                    {group.payments.length} claim{group.payments.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "var(--blue-900)" }}>{naira(groupTotal)}</div>
            </div>

            {isExpanded && (
              <div style={{ padding: "4px 4px 4px 30px" }}>
                {group.payments.map((p) => {
                  const age = ageInfo(p.submitted_at);
                  return (
                    <div className="pl-card" key={p.id} style={{ marginTop: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{p.equipment_name}</div>
                          <div className="pl-meta">submitted {formatDate(p.submitted_at)}</div>
                          <span className={`pl-chip pl-chip-${age.tone}`} style={{ marginTop: 8 }}>
                            <Clock size={11} /> {age.text}
                          </span>
                          {p.receipt_image && (
                            <ReceiptThumbnail url={p.receipt_image} onOpen={handleOpenReceipt} />
                          )}
                        </div>
                        <div style={{ fontWeight: 800, fontSize: 17, color: "var(--blue-900)", whiteSpace: "nowrap" }}>
                          {naira(p.amount)}
                        </div>
                      </div>

                      {rejectingId === p.id ? (
                        <div style={{ marginTop: 12, padding: 12, background: "var(--red-50)", border: "1px solid var(--red-100)", borderRadius: 8 }}>
                          <label style={{ fontSize: 12, fontWeight: 700, color: "var(--red-700)", display: "block", marginBottom: 6 }}>
                            Reason (optional, shown in admin panel only)
                          </label>
                          <textarea
                            style={{ width: "100%", border: "1px solid var(--line)", borderRadius: 6, padding: "8px 10px", fontFamily: "inherit", fontSize: 13, minHeight: 44 }}
                            value={rejectNote}
                            onChange={(e) => setRejectNote(e.target.value)}
                            placeholder="e.g. No matching transfer found"
                          />
                          <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
                            <button className="pl-btn pl-btn-ghost" onClick={() => { setRejectingId(null); setRejectNote(""); }}>
                              Cancel
                            </button>
                            <button className="pl-btn pl-btn-reject" disabled={busyId === p.id} onClick={() => handleReject(p)}>
                              <X size={14} /> Confirm rejection
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                          <button className="pl-btn pl-btn-confirm" disabled={busyId === p.id} onClick={() => handleConfirm(p)}>
                            <Check size={14} /> {busyId === p.id ? "Confirming..." : "Confirm Payment"}
                          </button>
                          <button className="pl-btn pl-btn-reject" disabled={busyId === p.id} onClick={() => setRejectingId(p.id)}>
                            <X size={14} /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {undo && (
        <div className="pl-undo-toast">
          <span>{undo.label}</span>
          <button className="pl-undo-btn" onClick={handleUndo}>
            <Undo2 size={13} /> Undo ({undo.secondsLeft}s)
          </button>
        </div>
      )}

      {openedReceipt && (
        <ReceiptModal
          objectUrl={openedReceipt.objectUrl}
          isPdf={openedReceipt.isPdf}
          onClose={() => setOpenedReceipt(null)}
        />
      )}
    </div>
  );
}